import React, { useState } from 'react';
import { CheckCircle, Trash2, Star, Pencil } from 'lucide-react';
import { ptsFor } from '../utils/points';
import { relativeDay } from '../utils/relativeDay';

const ChoreItem = ({ chore, onComplete, onDelete, onToggleFavorite, onEdit }) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleDoneClick = async () => {
    setIsAnimating(true);
    setTimeout(async () => {
      try {
        await onComplete();
      } finally {
        setIsAnimating(false);
      }
    }, 600);
  };

  const lastLog = (chore.logs || []).reduce((latest, l) =>
    !latest || new Date(l.doneAt) > new Date(latest.doneAt) ? l : latest, null);
  const lastDoer = lastLog?.user?.[0];
  const pts = ptsFor(chore.defaultMinutes, chore.defaultDifficulty);

  return (
    <div
      onClick={!isAnimating ? handleDoneClick : undefined}
      className={`flex items-center justify-between p-4 bg-ink-card border border-white/10 rounded-2xl mb-3 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer active:scale-[0.99] ${isAnimating ? 'chore-card-pulsing opacity-90' : ''}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {isAnimating ? (
          <CheckCircle className="text-mint w-6 h-6 flex-shrink-0" />
        ) : (
          <div
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: lastDoer?.color || 'rgba(245,242,236,.18)' }}
          />
        )}
        <div className="min-w-0">
          <p className={`text-ink-fg/90 ${isAnimating ? 'font-bold text-mint' : 'font-medium'} transition-colors truncate`}>
            {chore.title}
          </p>
          <p className="text-[10.5px] font-mono text-ink-fg/35 mt-0.5 truncate">
            {lastDoer ? `last: ${lastDoer.name}, ${relativeDay(lastLog.doneAt)}` : 'not logged yet'}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <span className="text-[10px] font-mono font-semibold text-ink-fg/40 px-2">
          {pts} PTS
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(chore.id, !chore.isFavorite); }}
          className={`p-2 rounded-full cursor-pointer focus:outline-none transition-colors active:scale-90 hover:bg-amber-400/10 ${chore.isFavorite ? 'text-amber-400' : 'text-ink-fg/25 hover:text-amber-400'}`}
        >
          <Star size={18} className={chore.isFavorite ? 'fill-amber-400' : ''} />
        </button>

        <button
          onClick={(e) => { e.stopPropagation(); onEdit(chore); }}
          className="text-ink-fg/30 transition-colors p-2 hover:bg-mint/10 hover:text-mint rounded-full cursor-pointer focus:outline-none active:scale-90"
        >
          <Pencil size={16} />
        </button>

        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-ink-fg/30 transition-colors p-2 hover:bg-danger/10 hover:text-danger rounded-full cursor-pointer focus:outline-none active:scale-90"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};

export default ChoreItem;
